import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import ChartHelpButton from '../ChartHelpButton';
import { formatLargeNumber, getRegionChartColor, getSmartYDomain } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { getLegendWrapperStyle } from './chartStyleUtils';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';
import { VolumeByDistrictTooltipBody } from './ChartTooltipContent';
import { useChartTooltipTrigger } from './useChartTooltipTrigger';
import ChartFullscreenTitleControl from '../analytics/ChartFullscreenTitleControl';
import { PresetChartTitle, PresetFilterStrip, PresetChartLegend, presetChartBodyClass, PRESET_FILTER_OPEN_PT } from '../analytics/PresetChartChrome';
import { useIsBelowLg, useIsPhone, getResponsiveChartMargin } from '../../hooks/useMediaQuery';
import { getChartYAxisWidth, getYAxisDomainLabels } from './chartStyleUtils';
import { singleSelectionLabel } from '../../../utils/filterLabels';
import { groupUsaSeries, toUsaOriginSet, isUsaOrigin, splitSeriesKey } from '../../utils/usaGrouping';

/**
 * Commodity Planner - Historic volume line/area chart
 * Supports year filter to compare volumes across multiple years
 */
const VolumeLineChart = ({
  data = { organic: [], conventional: [] },
  commodity = '',
  forceFiltersOpen = false,
  presetDescription = '',
  analyticsFullscreen = null,
  presetSingleChartFullscreen = false,
  hideHelpButton = false,
  hideLegend = false,
}) => {
  const { isReady: analyticsPrefsReady } = useWidgetOptionsPersistence('volume-line');
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  // Collapsed by default. The preset view keeps filters open from lg up via the
  // CSS gate on the strip below; smaller screens stay behind the Filters toggle.
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(['Conventional']);
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'weekly'
  /** District column keys (e.g. "2024_Mexico") — stable for Firestore restore; indices alone drift when yKeys reorder */
  const [selectedDistrictKeys, setSelectedDistrictKeys] = useState([]);
  /** User-facing origins (e.g. "Santa Maria") selected after year(s) */
  const [selectedOrigins, setSelectedOrigins] = useState([]);
  /** True only when the user explicitly cleared all origins (Clear All). Distinguishes from empty before defaults / snapshot restore. */
  const [districtsUserCleared, setDistrictsUserCleared] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  /** View-only: merge selected USA regions into one "USA" line. Selection stays per region. */
  const [groupUsa, setGroupUsa] = useState(false);

  // Modal states
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const initializedRef = useRef(false);
  const skipDistrictSyncRef = useRef(false);
  const pendingHydrateKeysRef = useRef(null);

  // Reset init guard when commodity scope changes so defaults can apply for new commodities.
  // Layout effect ensures this happens before the init effect can run.
  useLayoutEffect(() => {
    initializedRef.current = false;
    skipDistrictSyncRef.current = false;
    pendingHydrateKeysRef.current = null;
  }, [commodity]);

  // Fields that are metadata, not district/origin keys
  const METADATA_FIELDS = ['date_string', 'iso_week_number', 'type', 'year', 'label', 'district_weeks'];

  const mergeVolumeDatasets = useCallback((datasets) => {
    const rows = datasets.filter((d) => Array.isArray(d) && d.length > 0);
    if (rows.length === 0) return [];
    if (rows.length === 1) return rows[0];
    const byDate = new Map();
    rows.forEach((dataset) => {
      dataset.forEach((item) => {
        const dateKey = item?.date_string;
        if (dateKey == null) return;
        if (!byDate.has(dateKey)) {
          const copy = { ...item };
          // Clone the per-district week map so later merges don't mutate the source row.
          if (item.district_weeks) copy.district_weeks = { ...item.district_weeks };
          byDate.set(dateKey, copy);
          return;
        }
        const merged = byDate.get(dateKey);
        // Merge week maps across datasets (Organic + Conventional) so districts present in
        // only one dataset keep their own week instead of falling back to the shared one.
        if (item.district_weeks) {
          merged.district_weeks = { ...(merged.district_weeks || {}), ...item.district_weeks };
        }
        Object.keys(item).forEach((key) => {
          if (METADATA_FIELDS.includes(key)) return;
          const prev = Number(merged[key]) || 0;
          const next = Number(item[key]) || 0;
          merged[key] = prev + next;
        });
      });
    });
    return [...byDate.values()];
  }, []);

  // Helper to extract year from district key (e.g., "2024_Mexico" -> 2024)
  const getYearFromDistrict = (district) => {
    const match = district.match(/^(\d{4})_/);
    return match ? parseInt(match[1], 10) : null;
  };

  const getOriginFromDistrictKey = useCallback((districtKey) => {
    if (districtKey == null) return '';
    return String(districtKey).replace(/^\d{4}_/, '');
  }, []);

  const makeDistrictKey = useCallback((year, origin) => {
    if (!year || !origin) return '';
    return `${year}_${origin}`;
  }, []);

  const formatSeriesLabel = useCallback((districtKey) => {
    const year = getYearFromDistrict(districtKey);
    const origin = getOriginFromDistrictKey(districtKey);
    if (year && origin) return `${origin} (${year})`;
    return origin || String(districtKey ?? '');
  }, [getOriginFromDistrictKey]);

  // Helper: extract district keys from a dataset
  const extractDistrictKeys = (dataset) => {
    if (!dataset || dataset.length === 0) return new Set();
    const districts = new Set();
    dataset.forEach(item => {
      Object.keys(item).forEach(key => {
        if (!METADATA_FIELDS.includes(key) && item[key] !== undefined && item[key] !== null) {
          districts.add(key);
        }
      });
    });
    return districts;
  };

  // STEP 1: Extract available years from BOTH organic and conventional (year is top of cascade)
  const availableYears = useMemo(() => {
    const years = new Set();
    const allDistricts = new Set([
      ...extractDistrictKeys(data.organic),
      ...extractDistrictKeys(data.conventional),
    ]);
    allDistricts.forEach(district => {
      const year = getYearFromDistrict(district);
      if (year) years.add(year);
    });
    return [...years].sort((a, b) => b - a);
  }, [data]);

  // Do NOT clear initializedRef in useEffect([data]): effects run after useLayoutEffect and would let
  // the init effect overwrite Firestore-restored state before it persists again.

  const hydrateVolume = useCallback(
    (s) => {
      initializedRef.current = true;
      skipDistrictSyncRef.current = true;
      pendingHydrateKeysRef.current = Array.isArray(s.selectedDistrictKeys) ? s.selectedDistrictKeys : null;
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
      if (s.viewMode === 'monthly' || s.viewMode === 'weekly') setViewMode(s.viewMode);
      if (Array.isArray(s.selectedYears) && s.selectedYears.length) {
        const next = s.selectedYears.filter((yr) => availableYears.includes(yr));
        if (next.length) setSelectedYears(next);
      }
      if (Array.isArray(s.selectedTypes) && s.selectedTypes.length) {
        const next = s.selectedTypes.filter((t) => t === 'Organic' || t === 'Conventional');
        if (next.length) setSelectedTypes(next);
      } else if (s.selectedType === 'Organic' || s.selectedType === 'Conventional') {
        setSelectedTypes([s.selectedType]);
      }
      setDistrictsUserCleared(s.districtsUserCleared === true);
      setGroupUsa(s.groupUsa === true);
    },
    [availableYears]
  );

  const snapshotVolume = useCallback(
    () => ({
      filtersExpanded,
      selectedTypes,
      viewMode,
      selectedYears,
      selectedOrigins,
      selectedDistrictKeys,
      districtsUserCleared,
      groupUsa,
    }),
    [filtersExpanded, selectedTypes, viewMode, selectedYears, selectedOrigins, selectedDistrictKeys, districtsUserCleared, groupUsa]
  );

  usePersistWidgetSnapshot(
    'volume-line',
    commodity,
    !!(availableYears && availableYears.length > 0),
    hydrateVolume,
    snapshotVolume,
    [filtersExpanded, selectedTypes, viewMode, selectedYears, selectedOrigins, selectedDistrictKeys, districtsUserCleared, groupUsa]
  );

  // Defaults only when there is no Firestore snapshot; after analytics state is fetched.
  useEffect(() => {
    if (!analyticsPrefsReady || !availableYears || availableYears.length === 0 || initializedRef.current) return;
    initializedRef.current = true;
    // Defaults:
    // - Years: current + last year
    // - Type: Conventional
    // - Origins: top 2 this year (annual) + top 2 last year (annual) + top 1 past 30 days
    //            Union of those, up to 5 unique origins, shown for both selected years
    const currentYear = availableYears[0];
    const lastYear = currentYear - 1;
    const yearsDefault = availableYears.includes(lastYear) ? [currentYear, lastYear] : [currentYear];
    setSelectedYears(yearsDefault);
    setSelectedTypes(['Conventional']);
    setDistrictsUserCleared(false);

    const pickTopKeys = (rows, keys, limit = 2) => {
      const totals = {};
      keys.forEach((k) => {
        totals[k] = 0;
      });
      (rows || []).forEach((item) => {
        keys.forEach((k) => {
          totals[k] += Number(item?.[k]) || 0;
        });
      });
      return Object.entries(totals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, limit)
        .map(([k]) => k);
    };

    const baseTypeData = data?.conventional || [];
    const allKeys = extractDistrictKeys(baseTypeData);
    const yearKeys = (yr) => [...allKeys].filter((k) => getYearFromDistrict(k) === yr);

    const currentYearKeys = yearKeys(currentYear);
    const currentYearRows = baseTypeData.filter((item) => Number(item?.year) === currentYear);

    // Past 30 days window relative to the latest data point for this year
    const currentYearDates = currentYearRows
      .map((item) => new Date(item?.date_string))
      .filter((dt) => !Number.isNaN(dt.getTime()));
    const hasCurrentYearWindow = currentYearDates.length > 0;
    const maxCurrentYearDate = hasCurrentYearWindow
      ? new Date(Math.max(...currentYearDates.map((dt) => dt.getTime())))
      : null;
    const minCurrentYearDate = hasCurrentYearWindow
      ? new Date(maxCurrentYearDate.getTime() - (29 * 24 * 60 * 60 * 1000))
      : null;
    const rowsInLast30Days = hasCurrentYearWindow
      ? currentYearRows.filter((item) => {
          const dt = new Date(item?.date_string);
          if (Number.isNaN(dt.getTime())) return false;
          return dt >= minCurrentYearDate && dt <= maxCurrentYearDate;
        })
      : [];

    // Top 2 origins by this year's full annual volume
    const top2ThisYearOrigins = pickTopKeys(currentYearRows, currentYearKeys, 2)
      .map((k) => getOriginFromDistrictKey(k))
      .filter(Boolean);

    // Top 2 origins by last year's full annual volume (ranked independently)
    const lastYearKeys = yearKeys(lastYear);
    const lastYearRows = baseTypeData.filter((item) => Number(item?.year) === lastYear);
    const top2LastYearOrigins = availableYears.includes(lastYear)
      ? pickTopKeys(lastYearRows, lastYearKeys, 2)
          .map((k) => getOriginFromDistrictKey(k))
          .filter(Boolean)
      : [];

    // Top 1 origin by past 30 days volume (recent momentum)
    const top1Past30DaysOrigins = pickTopKeys(
      rowsInLast30Days.length > 0 ? rowsInLast30Days : currentYearRows,
      currentYearKeys,
      1
    )
      .map((k) => getOriginFromDistrictKey(k))
      .filter(Boolean);

    // Union all three sets, preserve priority order, unique, cap at 5
    const topOrigins = [
      ...new Set([...top2ThisYearOrigins, ...top2LastYearOrigins, ...top1Past30DaysOrigins]),
    ].slice(0, 5);

    // Build district keys: show all top origins for each selected year
    const selectedKeys = [];
    yearsDefault.forEach((yr) => {
      topOrigins.forEach((origin) => {
        const key = makeDistrictKey(yr, origin);
        if (allKeys.has(key)) selectedKeys.push(key);
      });
    });
    setSelectedDistrictKeys([...new Set(selectedKeys)]);
  }, [analyticsPrefsReady, availableYears, data, getOriginFromDistrictKey, makeDistrictKey]);

  // STEP 2: Determine available types for the selected years
  const availableTypes = useMemo(() => {
    const types = [];
    const hasYearData = (dataset) => {
      if (!dataset || dataset.length === 0) return false;
      const districts = extractDistrictKeys(dataset);
      return [...districts].some(d => {
        const year = getYearFromDistrict(d);
        return year && (selectedYears.length === 0 || selectedYears.includes(year));
      });
    };
    if (hasYearData(data.conventional)) types.push('Conventional');
    if (hasYearData(data.organic)) types.push('Organic');
    return types;
  }, [data, selectedYears]);

  // Auto-adjust selectedTypes if current selection is not available for the selected years
  useEffect(() => {
    if (availableTypes.length === 0) return;
    setSelectedTypes((prev) => {
      const kept = prev.filter((t) => availableTypes.includes(t));
      if (kept.length > 0) return kept;
      if (availableTypes.includes('Conventional')) return ['Conventional'];
      return [availableTypes[0]];
    });
  }, [availableTypes]);

  // STEP 3: Get data for the selected type(s) — sum organic + conventional when both are selected
  const typeData = useMemo(() => {
    const datasets = [];
    if (selectedTypes.includes('Conventional') && data.conventional?.length) {
      datasets.push(data.conventional);
    }
    if (selectedTypes.includes('Organic') && data.organic?.length) {
      datasets.push(data.organic);
    }
    return mergeVolumeDatasets(datasets);
  }, [data, selectedTypes, mergeVolumeDatasets]);

  // Extract all unique district keys from the selected type data
  const allDistrictKeys = useMemo(() => {
    const districts = extractDistrictKeys(typeData);
    if (districts.size === 0) return [];
    // Sort districts by year descending, then total volume descending within each year
    const districtTotals = {};
    districts.forEach(d => {
      districtTotals[d] = typeData.reduce((sum, item) => sum + (Number(item[d]) || 0), 0);
    });
    return [...districts].sort((a, b) => {
      const yearA = getYearFromDistrict(a) || 0;
      const yearB = getYearFromDistrict(b) || 0;
      if (yearB !== yearA) return yearB - yearA;
      return districtTotals[b] - districtTotals[a];
    });
  }, [typeData]);

  // STEP 4: Filter district keys by selected years
  const filteredDistrictKeys = useMemo(() => {
    if (selectedYears.length === 0) return allDistrictKeys;
    return allDistrictKeys.filter(district => {
      const year = getYearFromDistrict(district);
      return year && selectedYears.includes(year);
    });
  }, [allDistrictKeys, selectedYears]);

  // Aggregate data by month (only for filtered districts)
  const aggregateByMonth = (data, districts) => {
    if (!data || data.length === 0 || districts.length === 0) return [];

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData = {};

    data.forEach((item) => {
      const date = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
      const monthKey = months[date.getMonth()];

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {};
      }

      districts.forEach((district) => {
        if (item[district] !== undefined && item[district] !== null) {
          monthlyData[monthKey][district] = (monthlyData[monthKey][district] || 0) + Number(item[district]);
        }
      });
    });

    return months.map((monthKey) => {
      const entry = { label: monthKey };
      districts.forEach((district) => {
        entry[district] = monthlyData[monthKey]?.[district] || 0;
      });
      return entry;
    });
  };

  // Aggregate data by week (only for filtered districts)
  const aggregateByWeek = (data, districts) => {
    if (!data || data.length === 0 || districts.length === 0) return [];

    const weeklyData = {};

    data.forEach((item) => {
      districts.forEach((district) => {
        if (item[district] !== undefined && item[district] !== null) {
          // Bucket each district by ITS OWN ISO week. Season-overlay rows place multiple
          // original weeks under one display date, so a single shared item.iso_week_number
          // would shift current-/prior-year volume into the wrong week.
          const weekKey = item.district_weeks?.[district] ?? item.iso_week_number;
          if (weekKey == null) return;
          if (!weeklyData[weekKey]) weeklyData[weekKey] = {};
          weeklyData[weekKey][district] = (weeklyData[weekKey][district] || 0) + Number(item[district]);
        }
      });
    });

    const allWeeks = Array.from({ length: 52 }, (_, i) => i + 1);
    return allWeeks.map((weekKey) => {
      const entry = { label: String(weekKey) };
      districts.forEach((district) => {
        entry[district] = weeklyData[weekKey]?.[district] || 0;
      });
      return entry;
    });
  };

  // Process data based on view mode (using filtered districts)
  const processedData = useMemo(() => {
    if (viewMode === 'monthly') {
      return aggregateByMonth(typeData, filteredDistrictKeys);
    } else {
      return aggregateByWeek(typeData, filteredDistrictKeys);
    }
  }, [typeData, filteredDistrictKeys, viewMode]);

  // yKeys are the filtered district keys
  const yKeys = filteredDistrictKeys;

  // Origins list depends on selected years (year must be selected first).
  const availableOrigins = useMemo(() => {
    const keys = selectedYears.length > 0 ? yKeys : [];
    const set = new Set();
    keys.forEach((k) => {
      const origin = getOriginFromDistrictKey(k);
      if (origin) set.add(origin);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [yKeys, selectedYears, getOriginFromDistrictKey]);

  // Keep selectedOrigins in sync with selectedDistrictKeys (snapshot restore / back-compat).
  useEffect(() => {
    setSelectedOrigins((prev) => {
      if (prev.length > 0) return prev;
      if (!Array.isArray(selectedDistrictKeys) || selectedDistrictKeys.length === 0) return prev;
      const set = new Set(selectedDistrictKeys.map(getOriginFromDistrictKey).filter(Boolean));
      return [...set].sort((a, b) => a.localeCompare(b));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDistrictKeys, getOriginFromDistrictKey]);

  // Derive concrete district keys from (years × origins). This is what the chart actually plots.
  useEffect(() => {
    if (skipDistrictSyncRef.current) return;
    if (!selectedYears.length || !selectedOrigins.length) {
      setSelectedDistrictKeys([]);
      return;
    }
    if (districtsUserCleared) return;
    const next = [];
    selectedYears.forEach((yr) => {
      selectedOrigins.forEach((origin) => {
        const key = makeDistrictKey(yr, origin);
        if (yKeys.includes(key)) next.push(key);
      });
    });
    setSelectedDistrictKeys(next);
  }, [selectedYears, selectedOrigins, yKeys, makeDistrictKey, districtsUserCleared]);

  // Calculate top 4 districts by total volume
  const top4Districts = useMemo(() => {
    if (!processedData || processedData.length === 0 || yKeys.length === 0) return [];

    const districtTotals = {};
    yKeys.forEach((district) => {
      districtTotals[district] = processedData.reduce((sum, item) => sum + (Number(item[district]) || 0), 0);
    });

    return Object.entries(districtTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([district]) => yKeys.indexOf(district))
      .filter((index) => index !== -1);
  }, [processedData, yKeys]);

  // Restore districts from Firestore, prune invalid keys when yKeys change, or default to top 4
  useEffect(() => {
    if (skipDistrictSyncRef.current) {
      skipDistrictSyncRef.current = false;
      const keys = pendingHydrateKeysRef.current;
      pendingHydrateKeysRef.current = null;
      if (keys && keys.length > 0) {
        const valid = keys.filter((k) => yKeys.includes(k));
        if (valid.length > 0) {
          setSelectedDistrictKeys(valid);
          // Derive origins for UX display.
          const originSet = new Set(valid.map(getOriginFromDistrictKey).filter(Boolean));
          setSelectedOrigins([...originSet].sort((a, b) => a.localeCompare(b)));
          return;
        }
      }
    }
    setSelectedDistrictKeys((prev) => {
      const kept = prev.filter((k) => yKeys.includes(k));
      if (kept.length > 0) return kept;
      // If user explicitly cleared all, keep empty (do not re-default to top 4),
      // even in preset views.
      if (districtsUserCleared) return [];
      // In preset view (forceFiltersOpen), always default to a visible chart even if the
      // user previously cleared all in the draggable/custom view.
      if (forceFiltersOpen && top4Districts.length > 0) {
        const defaults = top4Districts.map((i) => yKeys[i]).filter(Boolean);
        const originSet = new Set(defaults.map(getOriginFromDistrictKey).filter(Boolean));
        setSelectedOrigins([...originSet].sort((a, b) => a.localeCompare(b)));
        return defaults;
      }
      if (top4Districts.length > 0) {
        const defaults = top4Districts.map((i) => yKeys[i]).filter(Boolean);
        const originSet = new Set(defaults.map(getOriginFromDistrictKey).filter(Boolean));
        setSelectedOrigins([...originSet].sort((a, b) => a.localeCompare(b)));
        return defaults;
      }
      return [];
    });
  }, [filteredDistrictKeys, top4Districts, yKeys, districtsUserCleared, forceFiltersOpen, getOriginFromDistrictKey]);

  // Handler for year toggle
  const handleYearToggle = (year) => {
    if (selectedYears.includes(year)) {
      setSelectedYears(selectedYears.filter(y => y !== year));
    } else {
      setSelectedYears([...selectedYears, year]);
    }
  };

  const getYearsDisplayText = () => {
    if (selectedYears.length === 0) return t('filters.selectYears');
    const only = singleSelectionLabel(selectedYears, availableYears);
    if (only) return only;
    if (selectedYears.length === availableYears.length) return t('filters.allYears');
    if (selectedYears.length > 3) return t('filters.nSelected', { count: selectedYears.length });
    return selectedYears.sort((a, b) => b - a).join(', ');
  };

  // Selected districts in yKeys order (for series + tooltip)
  const selectedYKeys = useMemo(() => {
    return yKeys.filter((k) => selectedDistrictKeys.includes(k));
  }, [yKeys, selectedDistrictKeys]);

  // Generate chart colors
  const chartColors = useMemo(() => {
    return yKeys.map((_, index) => getRegionChartColor(index, yKeys.length));
  }, [yKeys]);

  // USA region grouping (view-only). Origins the backend flagged as USA regions.
  const usaOriginSet = useMemo(() => toUsaOriginSet(data?.usaOrigins), [data]);
  const availableUsaOrigins = useMemo(
    () => availableOrigins.filter((o) => isUsaOrigin(o, usaOriginSet)),
    [availableOrigins, usaOriginSet]
  );
  const otherOrigins = useMemo(
    () => availableOrigins.filter((o) => !isUsaOrigin(o, usaOriginSet)),
    [availableOrigins, usaOriginSet]
  );
  const hasSelectedUsaRegion = useMemo(
    () => selectedYKeys.some((k) => isUsaOrigin(splitSeriesKey(k).origin, usaOriginSet)),
    [selectedYKeys, usaOriginSet]
  );
  const usaGroupingActive = groupUsa && hasSelectedUsaRegion;

  // What is actually plotted: per-region series, or one `{year}_USA` series per year.
  const display = useMemo(() => {
    if (!usaGroupingActive) {
      return { rows: processedData, selectedKeys: selectedYKeys, allKeys: yKeys, colors: chartColors };
    }
    const grouped = groupUsaSeries(processedData, selectedYKeys, usaOriginSet);
    const colorByKey = new Map(yKeys.map((k, i) => [k, chartColors[i]]));
    // A grouped line reuses the colour of the first region it merges, so it keeps
    // the same hue for the year when the checkbox is toggled.
    grouped.usaKeys.forEach((groupKey) => {
      colorByKey.set(groupKey, colorByKey.get(grouped.regionKeysByGroup[groupKey][0]));
    });
    const allKeys = [...yKeys.filter((k) => grouped.keys.includes(k)), ...grouped.usaKeys];
    return {
      rows: grouped.rows,
      selectedKeys: grouped.keys,
      allKeys,
      colors: allKeys.map((k) => colorByKey.get(k)),
    };
  }, [usaGroupingActive, processedData, selectedYKeys, yKeys, chartColors, usaOriginSet]);

  const chartTooltipBoundaryRef = useRef(null);
  const { trigger: tooltipTrigger, tooltipDismissKey } = useChartTooltipTrigger(chartTooltipBoundaryRef);
  const isBelowLg = useIsBelowLg();
  const isPhone = useIsPhone();

  // Phones size the volume axis to the labels it actually draws instead of the
  // fixed 72px, which is mostly empty at the smaller phone tick size.
  //
  // Preset views only: the widget dashboard renders a rotated axis title in
  // that same column (`volumeYAxisLabelProps` below). Rotated about its centre,
  // that title's horizontal extent sits mid-column, so narrowing the column
  // would run it straight into the tick labels.
  const compactYAxis = isPhone && forceFiltersOpen;
  const volumeYDomain = useMemo(
    () => getSmartYDomain(display.rows, display.selectedKeys),
    [display]
  );
  const volumeYAxisWidth = useMemo(
    () =>
      getChartYAxisWidth({
        isPhone: compactYAxis,
        fontSize,
        labels: getYAxisDomainLabels(volumeYDomain, formatLargeNumber),
      }),
    [compactYAxis, fontSize, volumeYDomain]
  );

  const volumeYAxisLabelProps = useMemo(
    () => ({
      value: t('filters.volumeLbs'),
      angle: -90,
      position: 'insideLeft',
      dx: -12,
      fill: theme.axisLabelFill,
      fontSize,
    }),
    [t, theme.axisLabelFill, fontSize]
  );

  const volumeTooltipContent = useCallback(
    (props) => (
      <VolumeByDistrictTooltipBody
        {...props}
        theme={theme}
        fontSize={fontSize}
        selectedYKeys={display.selectedKeys}
        yKeys={display.allKeys}
        chartColors={display.colors}
        viewMode={viewMode}
      />
    ),
    [theme, fontSize, display, viewMode]
  );

  // Legend grouped into one row per year. Every series name carries its year
  // ("Mexico (2025)"), so a flat legend repeats the year on all 15-odd entries;
  // grouping hoists it into a single heading per block and drops the suffix.
  const renderYearGroupedLegend = useCallback(
    ({ payload }) => {
      if (!payload || payload.length === 0) return null;

      const groups = new Map();
      payload.forEach((entry) => {
        const districtKey = entry?.dataKey ?? '';
        const year = getYearFromDistrict(String(districtKey));
        // Keys without a year prefix keep their own bucket at the end of the list.
        const bucket = year ?? '';
        if (!groups.has(bucket)) groups.set(bucket, []);
        groups.get(bucket).push({
          key: String(districtKey),
          color: entry?.color,
          label: getOriginFromDistrictKey(districtKey) || entry?.value || String(districtKey),
        });
      });

      const orderedYears = [...groups.keys()].sort((a, b) => {
        if (a === '') return 1;
        if (b === '') return -1;
        return b - a;
      });

      return (
        <div className="flex flex-col gap-y-1 px-1" style={{ fontSize, color: theme.legendText }}>
          {orderedYears.map((year) => (
            <div key={year || 'other'} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {year !== '' && (
                <span className="font-semibold tabular-nums opacity-90">
                  {year}
                </span>
              )}
              {groups.get(year).map((item) => (
                <span key={item.key} className="inline-flex items-center gap-x-1.5">
                  <span
                    className="inline-block rounded-full shrink-0"
                    style={{ width: 8, height: 8, backgroundColor: item.color }}
                  />
                  {item.label}
                </span>
              ))}
            </div>
          ))}
        </div>
      );
    },
    [fontSize, theme.legendText, getOriginFromDistrictKey]
  );

  const handleTypeToggle = (type) => {
    setSelectedTypes((prev) => {
      if (prev.includes(type)) {
        const next = prev.filter((t) => t !== type);
        return next.length > 0 ? next : prev;
      }
      return [...prev, type];
    });
  };

  const getTypeDisplayText = () => {
    if (selectedTypes.length === 0) return t('filters.selectTypes');
    const only = singleSelectionLabel(selectedTypes, availableTypes);
    if (only) return only;
    if (selectedTypes.length === availableTypes.length && availableTypes.length > 0) {
      return t('filters.allTypes');
    }
    return t('filters.nSelected', { count: selectedTypes.length });
  };

  const toggleOrigin = (origin) => {
    if (!origin) return;
    setSelectedOrigins((prev) => {
      const next = prev.includes(origin) ? prev.filter((o) => o !== origin) : [...prev, origin];
      setDistrictsUserCleared(next.length === 0);
      return next;
    });
  };

  const toggleUsaGroup = () => {
    const allSelected = availableUsaOrigins.every((o) => selectedOrigins.includes(o));
    const next = allSelected
      ? selectedOrigins.filter((o) => !availableUsaOrigins.includes(o))
      : [...new Set([...selectedOrigins, ...availableUsaOrigins])];
    setSelectedOrigins(next);
    setDistrictsUserCleared(next.length === 0);
  };

  const originModalGroups = availableUsaOrigins.length > 0
    ? [{ id: 'usa', label: t('charts.commodityPlanner.usaGroupLabel'), options: availableUsaOrigins }]
    : [];

  // Chip-style switch that lines up with the neighbouring filter controls
  // (same label-over-control structure and height as FilterDropdown).
  const renderUsaGroupToggle = (compact) => {
    if (availableUsaOrigins.length === 0) return null;
    const disabled = !hasSelectedUsaRegion;
    return (
      <div className="min-w-0 lg:shrink-0">
        {/* Invisible label keeps the control on the same baseline as the dropdowns */}
        <span
          aria-hidden="true"
          className="mb-[4px] block select-none text-[11px] font-medium opacity-0"
          style={compact ? undefined : { fontSize: fontSize - 2 }}
        >
          &nbsp;
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={groupUsa}
          disabled={disabled}
          onClick={() => setGroupUsa((v) => !v)}
          className={`flex w-full items-center gap-2 rounded-[8px] border px-[10px] text-left transition-colors lg:w-auto whitespace-nowrap ${
            compact ? 'h-[27px] text-[11px]' : 'h-[42px] text-sm'
          } ${
            groupUsa
              ? 'border-bloom-green bg-bloom-green/10 text-[#282A29] dark:text-white'
              : 'border-[#E2E2E2] text-[#282A29] dark:border-[#3a3c3b] dark:text-white'
          } ${
            disabled
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer hover:border-bloom-green dark:hover:border-green-500/50'
          }`}
        >
          <span
            className={`relative inline-flex h-[14px] w-[26px] shrink-0 items-center rounded-full transition-colors ${
              groupUsa ? 'bg-bloom-green' : 'bg-[#C9CDCB] dark:bg-[#4a4c4b]'
            }`}
          >
            <span
              className={`inline-block h-[10px] w-[10px] rounded-full bg-white shadow transition-transform ${
                groupUsa ? 'translate-x-[14px]' : 'translate-x-[2px]'
              }`}
            />
          </span>
          {t('charts.commodityPlanner.groupUsa')}
        </button>
      </div>
    );
  };

  const helpDescription = "Daily volume trends comparing organic vs conventional supply. Track market availability patterns and identify supply fluctuations over time.";

  // Single-chart fullscreen: below lg the overlay scrolls, so these boxes grow
  // with their content instead of being squeezed to 0px, and the plot keeps a
  // real height. See the `.preset-fullscreen-*` block in index.css.
  const fsFill = presetSingleChartFullscreen ? ' preset-fullscreen-fill' : '';
  const fsPlot = presetSingleChartFullscreen ? ' preset-fullscreen-plot' : '';

  if (data.organic.length === 0 && data.conventional.length === 0) {
    return (
      <div
        className={
          (forceFiltersOpen
            ? presetSingleChartFullscreen
              ? 'w-full h-full min-h-0 flex-1'
              : 'w-full'
            : 'h-full') + ' flex flex-col bg-transparent'
        }
      >
        <div className={`flex-1 flex flex-col min-h-0 ${presetChartBodyClass(forceFiltersOpen, presetSingleChartFullscreen)}`}>
          {/* Title */}
          <PresetChartTitle
            forceFiltersOpen={forceFiltersOpen}
            title={t('charts.commodityPlanner.title')}
            theme={theme}
            fontSize={fontSize}
            className={forceFiltersOpen ? '' : 'border-y py-2 px-4'}
            titleAfter={!hideHelpButton ? <ChartHelpButton title="Commodity Planner" description={helpDescription} /> : null}
            right={
              <ChartFullscreenTitleControl
                analyticsFullscreen={analyticsFullscreen}
                presetSingleChartFullscreen={presetSingleChartFullscreen}
              />
            }
          />

        <div className="flex-1 flex items-center justify-center">
          <p className="text-center" style={{ fontSize, color: theme.noDataText }}>
            {t('charts.commodityPlanner.noData')}<br />{t('charts.selectAnotherTimeframe')}
          </p>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        (forceFiltersOpen
          ? presetSingleChartFullscreen
            ? 'w-full h-full min-h-0 flex-1' + fsFill
            : 'w-full'
          : 'h-full') + ' flex flex-col bg-transparent'
      }
    >
      <div className={`flex-1 flex flex-col min-h-0${fsFill} ${presetChartBodyClass(forceFiltersOpen, presetSingleChartFullscreen)}`}>
        {/* Title */}
        <PresetChartTitle
          forceFiltersOpen={forceFiltersOpen}
          title={t('charts.commodityPlanner.title')}
          theme={theme}
          fontSize={fontSize}
          titleAfter={!hideHelpButton ? <ChartHelpButton title="Commodity Planner" description={helpDescription} /> : null}
          right={
            <ChartFullscreenTitleControl
              analyticsFullscreen={analyticsFullscreen}
              presetSingleChartFullscreen={presetSingleChartFullscreen}
            />
          }
        />

        {/* Filters */}
        <div className="flex-shrink-0">
          {/* Collapsible in the widget dashboard, and below lg in the preset view
              too; lg:hidden once forceFiltersOpen since desktop preset keeps the
              strip open via the CSS gate below. */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className={`w-full flex items-center justify-between py-2 border-b flex-shrink-0 ${forceFiltersOpen ? 'lg:hidden' : ''}`}
            style={{ borderColor: theme.borderSecondary }}
          >
            <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('filters.filters')}</span>
            {filtersExpanded ? <ChevronUp size={16} style={{ color: theme.secondaryText }} /> : <ChevronDown size={16} style={{ color: theme.secondaryText }} />}
          </button>

          {(forceFiltersOpen || filtersExpanded) && (
            <div className={forceFiltersOpen ? (filtersExpanded ? PRESET_FILTER_OPEN_PT : 'max-lg:hidden') : undefined}>
            <PresetFilterStrip
              forceFiltersOpen={forceFiltersOpen}
              presetSingleChartFullscreen={presetSingleChartFullscreen}
              analyticsFullscreen={analyticsFullscreen}
              titleHasFullscreenControl
              gridClassName="pt-2 space-y-2"
            >
              {forceFiltersOpen ? (
                <>
                  <FilterDropdown
                    label={t('filters.years')}
                    value={getYearsDisplayText()}
                    onClick={() => setYearModalOpen(true)}
                    compact
                  />
                  <FilterDropdown
                    label={t('filters.type')}
                    value={getTypeDisplayText()}
                    onClick={() => setTypeModalOpen(true)}
                    compact
                  />
                  <FilterDropdown
                    label={t('filters.origin')}
                    options={availableOrigins}
                    selectedValues={selectedOrigins}
                    allLabel={t('filters.allOrigins')}
                    placeholder={selectedYears.length ? t('filters.selectOrigins') : t('filters.selectYears')}
                    getOptionLabel={(x) => x}
                    onClick={() => setOriginModalOpen(true)}
                    disabled={!selectedYears.length}
                    compact
                  />
                  {renderUsaGroupToggle(true)}
                  <FilterDropdown
                    label={t('charts.commodityPlanner.groupBy')}
                    value={viewMode === 'monthly' ? t('charts.commodityPlanner.month') : t('charts.commodityPlanner.week')}
                    onClick={() => setViewMode(viewMode === 'monthly' ? 'weekly' : 'monthly')}
                    compact
                  />
                </>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  <FilterDropdown
                    label={t('filters.years')}
                    value={getYearsDisplayText()}
                    onClick={() => setYearModalOpen(true)}
                  />
                  <FilterDropdown
                    label={t('filters.type')}
                    value={getTypeDisplayText()}
                    onClick={() => setTypeModalOpen(true)}
                  />
                  <FilterDropdown
                    label={t('filters.origin')}
                    options={availableOrigins}
                    selectedValues={selectedOrigins}
                    allLabel={t('filters.allOrigins')}
                    placeholder={selectedYears.length ? t('filters.selectOrigins') : t('filters.selectYears')}
                    getOptionLabel={(x) => x}
                    onClick={() => setOriginModalOpen(true)}
                    disabled={!selectedYears.length}
                  />
                  {renderUsaGroupToggle(false)}
                  <FilterDropdown
                    label={t('charts.commodityPlanner.groupBy')}
                    value={viewMode === 'monthly' ? t('charts.commodityPlanner.month') : t('charts.commodityPlanner.week')}
                    onClick={() => setViewMode(viewMode === 'monthly' ? 'weekly' : 'monthly')}
                  />
                </div>
              )}
            </PresetFilterStrip>
            </div>
          )}
        </div>

        {/* Result — same plot box chrome as the other preset charts (FOB / Terminal
            Price Pulse, Volume by Origin) so the plot sits on #191919 in dark mode. */}
        <div
          className={`${
            forceFiltersOpen && !presetSingleChartFullscreen
              ? 'preset-chart-plot-tall flex-shrink-0'
              : 'flex-1 min-h-0'
          }${fsPlot} bg-white dark:bg-[#191919] rounded-lg`}
          ref={chartTooltipBoundaryRef}
        >
          {processedData.length > 0 && selectedDistrictKeys.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={display.rows}
                margin={getResponsiveChartMargin({ isBelowLg })}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.gridColor} />
                <XAxis
                  dataKey="label"
                  stroke={theme.axisStroke}
                  angle={viewMode === 'weekly' ? -45 : 0}
                  textAnchor={viewMode === 'weekly' ? 'end' : 'middle'}
                  height={viewMode === 'weekly' ? 60 : 30}
                  tick={{ fontSize, fill: theme.tickFill }}
                  tickFormatter={(v) => {
                    if (v == null) return '';
                    const s = String(v);
                    return s.includes('NaN') || s.includes('undefined') ? '' : s;
                  }}
                />
                <YAxis
                  width={volumeYAxisWidth}
                  stroke={theme.axisStroke}
                  domain={volumeYDomain}
                  allowDataOverflow={false}
                  tickFormatter={formatLargeNumber}
                  tick={{ fontSize, fill: theme.tickFill }}
                  label={volumeYAxisLabelProps}
                />
                <Tooltip
                  key={tooltipDismissKey}
                  trigger={tooltipTrigger}
                  throttleDelay={50}
                  content={volumeTooltipContent}
                />
                {hideLegend && presetDescription ? null : (
                <Legend
                  verticalAlign="bottom"
                  content={renderYearGroupedLegend}
                  wrapperStyle={getLegendWrapperStyle({
                    fontSize,
                    paddingTop: 0,
                    bottom: forceFiltersOpen && !presetSingleChartFullscreen ? 0 : 6,
                    // Below lg the year-grouped legend runs several rows deep, so it
                    // keeps its own scroll box (`scrollable` opts it out of the mobile
                    // legend-wrap rule that would otherwise let it spill past the card)
                    // and reclaims the side gutters the narrow plot can't spare.
                    scrollable: true,
                    ...(isBelowLg ? { left: 8, right: 8 } : null),
                    maxHeight: isBelowLg ? 76 : 60,
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                  })}
                />
                )}
                {display.selectedKeys.map((district) => {
                  const originalIndex = display.allKeys.indexOf(district);
                  const color = display.colors[originalIndex];
                  return (
                    <Area
                      key={district}
                      type="monotone"
                      dataKey={district}
                      name={formatSeriesLabel(district)}
                      stroke={color}
                      fill={color}
                      fillOpacity={0.3}
                      strokeWidth={2}
                      connectNulls
                    />
                  );
                })}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm" style={{ color: theme.secondaryText }}>
                {selectedDistrictKeys.length === 0
                  ? t('charts.selectAtLeastOneOrigin')
                  : t('charts.noDataFilters')}
              </p>
            </div>
          )}
        </div>
        {hideLegend && presetDescription && processedData.length > 0 && selectedDistrictKeys.length > 0 ? (
          <PresetChartLegend theme={theme} fullscreen={presetSingleChartFullscreen}>
            <div className="max-h-40 w-full overflow-y-auto">
              <div className="flex flex-wrap items-center justify-start gap-x-5 gap-y-2">
                {[...display.selectedKeys]
                  .sort((a, b) => {
                    const ya = Number(getYearFromDistrict(a)) || 0;
                    const yb = Number(getYearFromDistrict(b)) || 0;
                    if (yb !== ya) return yb - ya;
                    return getOriginFromDistrictKey(a).localeCompare(getOriginFromDistrictKey(b));
                  })
                  .map((district) => {
                    const color = display.colors[display.allKeys.indexOf(district)];
                    return (
                      <div key={district} className="flex min-w-0 items-start gap-2 max-lg:w-full lg:shrink-0" role="listitem">
                        <svg width={24} height={10} className="shrink-0 block" aria-hidden>
                          <rect x={2} y={1} width={20} height={8} rx={2} fill={color} fillOpacity={0.3} stroke={color} strokeWidth={1.5} />
                        </svg>
                        <span
                          className="min-w-0 whitespace-normal break-words text-left leading-snug"
                          style={{ fontSize: Math.max(fontSize - 1, 10), color: theme.tickFill }}
                        >
                          {formatSeriesLabel(district)}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </PresetChartLegend>
        ) : null}
      </div>


      {/* Modals */}
      <FilterModal
        isOpen={yearModalOpen}
        onClose={() => setYearModalOpen(false)}
        title={t('filters.selectYears')}
        options={availableYears.map(String)}
        selectedValues={selectedYears.map(String)}
        onSelect={(yearStr) => handleYearToggle(Number(yearStr))}
        multiSelect
        onSelectAll={() => setSelectedYears([...availableYears])}
        onClearAll={() => setSelectedYears([])}
      />

      <FilterModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        title={t('filters.selectTypes')}
        options={availableTypes}
        selectedValues={selectedTypes}
        onSelect={handleTypeToggle}
        multiSelect
        onSelectAll={() => setSelectedTypes([...availableTypes])}
        onClearAll={() => {
          if (availableTypes.includes('Conventional')) setSelectedTypes(['Conventional']);
          else if (availableTypes.length > 0) setSelectedTypes([availableTypes[0]]);
        }}
      />

      <FilterModal
        isOpen={originModalOpen}
        onClose={() => setOriginModalOpen(false)}
        title={t('filters.selectOrigins')}
        options={originModalGroups.length > 0 ? otherOrigins : availableOrigins}
        groups={originModalGroups}
        onToggleGroup={toggleUsaGroup}
        selectedValues={selectedOrigins}
        onSelect={(origin) => toggleOrigin(origin)}
        multiSelect
        onSelectAll={() => {
          setSelectedOrigins([...availableOrigins]);
          setDistrictsUserCleared(false);
        }}
        onClearAll={() => {
          setSelectedOrigins([]);
          setDistrictsUserCleared(true);
        }}
      />
    </div>
  );
};

export default VolumeLineChart;

